import React, { useState } from "react";

const normalizeEntries = (entries) =>
  entries.map((entry) => entry.trim()).filter(Boolean);

export default function ProductChipListField({
  id,
  label,
  placeholder,
  values,
  onChange,
  className = "",
}) {
  const [draft, setDraft] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const entries = normalizeEntries(values);

  const addEntries = () => {
    const additions = draft
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (!additions.length) return;
    onChange([...entries, ...additions]);
    setDraft("");
  };

  const reorderEntry = (targetIndex) => {
    if (
      draggedIndex === null ||
      draggedIndex === targetIndex ||
      draggedIndex < 0 ||
      draggedIndex >= entries.length
    ) {
      return;
    }
    const reordered = [...entries];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    onChange(reordered);
    setDraggedIndex(targetIndex);
  };

  return (
    <div className={`min-w-0 ${className}`}>
      <span className="mb-1 block text-sm font-medium text-zinc-700">
        {label}
      </span>
      {entries.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1" aria-label={`${label} added`}>
          {entries.map((entry, index) => (
            <li
              key={`${entry}-${index}`}
              draggable
              onDragStart={(event) => {
                setDraggedIndex(index);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(index));
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                reorderEntry(index);
                setDraggedIndex(null);
              }}
              onDragEnd={() => setDraggedIndex(null)}
              title="Drag to reorder"
              className={`inline-flex cursor-grab items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs active:cursor-grabbing ${
                draggedIndex === index ? "opacity-60 ring-2 ring-blue-500" : ""
              }`}
            >
              <span aria-hidden="true" className="text-zinc-400">
                ⠿
              </span>
              {entry}
              <button
                type="button"
                aria-label={`Remove ${entry}`}
                onClick={() =>
                  onChange(
                    entries.filter((_, entryIndex) => entryIndex !== index),
                  )
                }
                className="rounded-full px-1 text-zinc-600 hover:bg-zinc-200"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <label className="sr-only" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              addEntries();
            }
          }}
          className="min-w-0 flex-1 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
        <button
          type="button"
          onClick={addEntries}
          disabled={!draft.trim()}
          className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}
